import React, { useMemo, useRef, useState, useEffect } from 'react';
import { SlidersHorizontal, RotateCcw, X, Plus, GripVertical, LayoutDashboard } from 'lucide-react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { format } from 'date-fns';
import { formatDate } from '../lib/dates';
import { useTranslation } from '../lib/i18n';
import { useConfirm } from '../lib/ConfirmDialog';
import WidgetGallery from './dashboard/WidgetGallery';
import { getWidgetBody } from './dashboard/registry';
import {
  getVariant,
  getCategoryForVariant,
  availableCategories,
  resolveInstances,
  isWidgetVisible,
  instanceLabel,
  mobileHeight,
  newWidgetId
} from './dashboard/catalog';

const ResponsiveGrid = WidthProvider(Responsive);

// Grid geometry — 12 columns on desktop, a single column on phones.
const COLS = { lg: 12, xs: 1 };
const BREAKPOINTS = { lg: 700, xs: 0 };
const ROW_HEIGHT = 44;

// The dashboard everyone starts with: day agenda + to-dos on top, week view
// and shopping list below. Scopes resolve against what the user actually
// has — with the personal calendar off, the calendar widgets point at the
// first shared calendar instead of vanishing — and a preferred widget whose
// feature is switched off is replaced by one from another category, so a
// reset never lands on an almost-empty dashboard.
const PREFERRED_DEFAULTS = ['calendar.day', 'todo.open', 'calendar.week', 'shopping.list'];
const DEFAULT_SLOTS = 4;

const scopeFor = (variantId, data) => {
  const cat = getCategoryForVariant(variantId);
  if (!cat || !cat.instances) return null;
  const instances = resolveInstances(cat, data);
  return instances.length ? instances[0].id : null;
};

const buildDefaultDashboard = (data) => {
  const widgets = [];
  const take = (variantId) => {
    if (widgets.length >= DEFAULT_SLOTS) return;
    if (!getVariant(variantId) || widgets.some((w) => w.variant === variantId)) return;
    const candidate = { id: newWidgetId(), variant: variantId, scope: scopeFor(variantId, data) };
    if (isWidgetVisible(candidate, data)) widgets.push(candidate);
  };
  PREFERRED_DEFAULTS.forEach(take);
  for (const cat of availableCategories(data)) {
    for (const v of cat.variants) take(v.id);
  }

  const lg = [];
  const xs = [];
  let lgY = 0;
  let xsY = 0;
  for (const w of widgets) {
    const variant = getVariant(w.variant);
    const size = variant.sizes[variant.defaultSize] || Object.values(variant.sizes)[0];
    lg.push({
      i: w.id, x: lg.length % 2 === 0 ? 0 : 6, y: lgY,
      w: Math.min(size.w, 6), h: size.h, minW: variant.min?.w, minH: variant.min?.h
    });
    if (lg.length % 2 === 0) lgY += size.h;
    const mh = mobileHeight(variant);
    xs.push({ i: w.id, x: 0, y: xsY, w: 1, h: mh, minH: variant.min?.h });
    xsY += mh;
  }
  return { version: 4, widgets, layouts: { lg, xs } };
};

// v1 stored only a fixed set of four widget ids plus a `hidden` list. Map
// those onto the v2 instance model, keeping the user's positions.
const V1_VARIANTS = {
  day: { variant: 'calendar.day', scope: 'personal' },
  todos: { variant: 'todo.open', scope: 'all' },
  week: { variant: 'calendar.week', scope: 'personal' },
  shopping: { variant: 'shopping.list', scope: 'personal' }
};

// v2 stored the desktop height for the phone layout too, so a small stat
// tile filled half the screen. v3 recomputes every phone height from the
// widget's own smallest size; positions and the desktop layout are kept.
const migrateMobileHeights = (stored) => {
  const xs = (stored.layouts?.xs || []).map((item) => {
    const w = (stored.widgets || []).find((x) => x.id === item.i);
    const variant = w && getVariant(w.variant);
    return variant ? { ...item, h: mobileHeight(variant) } : item;
  });
  // Re-stack so the new heights don't leave gaps.
  let y = 0;
  const stacked = xs.map((item) => {
    const next = { ...item, y };
    y += item.h;
    return next;
  });
  return { ...stored, version: 3, layouts: { ...stored.layouts, xs: stacked } };
};

// v4 shrinks the week widget: its grid stretched to the widget's height, so
// a stored h=8 produced day columns several times taller than the calendar
// page's own week row.
const clampWeekHeights = (stored) => {
  const clamp = (items, mobile) =>
    (items || []).map((item) => {
      const w = (stored.widgets || []).find((x) => x.id === item.i);
      if (!w || w.variant !== 'calendar.week') return item;
      const variant = getVariant(w.variant);
      if (!variant) return item;
      const cap = mobile
        ? mobileHeight(variant)
        : (variant.sizes[variant.defaultSize] || Object.values(variant.sizes)[0]).h;
      return { ...item, h: Math.min(item.h, cap) };
    });
  return {
    ...stored,
    version: 4,
    layouts: {
      ...stored.layouts,
      lg: clamp(stored.layouts?.lg, false),
      xs: clamp(stored.layouts?.xs, true)
    }
  };
};

const migrate = (stored, data) => {
  if (!stored || typeof stored !== 'object') return buildDefaultDashboard(data);
  if (stored.version === 4 && Array.isArray(stored.widgets)) return stored;
  if (stored.version === 3 && Array.isArray(stored.widgets)) return clampWeekHeights(stored);
  if (stored.version === 2 && Array.isArray(stored.widgets)) {
    return clampWeekHeights(migrateMobileHeights(stored));
  }
  const hidden = Array.isArray(stored.hidden) ? stored.hidden : [];
  const widgets = Object.keys(V1_VARIANTS)
    .filter((id) => !hidden.includes(id))
    .map((id) => ({ id, ...V1_VARIANTS[id] }));
  return clampWeekHeights(
    migrateMobileHeights({
      version: 2,
      widgets,
      layouts: stored.layouts || buildDefaultDashboard(data).layouts
    })
  );
};

// Next free row in a breakpoint's layout, so a new widget lands at the bottom.
const bottomOf = (items = []) =>
  items.reduce((max, it) => Math.max(max, (it.y || 0) + (it.h || 1)), 0);

const Dashboard = ({ data, updateData }) => {
  const { t, lang } = useTranslation();
  const confirm = useConfirm();

  // Refresh the "today" anchor every minute so a PWA left open across
  // midnight rolls its day/week widgets over instead of freezing.
  const [todayKey, setTodayKey] = useState(() => format(new Date(), 'yyyy-MM-dd HH:mm'));
  useEffect(() => {
    const id = setInterval(
      () => setTodayKey(format(new Date(), 'yyyy-MM-dd HH:mm')),
      60 * 1000
    );
    return () => clearInterval(id);
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const today = useMemo(() => new Date(), [todayKey]);

  const [editMode, setEditMode] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [breakpoint, setBreakpoint] = useState('lg');

  const dashboard = useMemo(
    () => migrate(data.settings?.dashboardLayout, data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.settings?.dashboardLayout, data]
  );

  // react-grid-layout can fire onLayoutChange with the render-time closure
  // right after a widget is removed, which wrote the widget straight back.
  // save() updates this ref immediately and handlers merge into it.
  const dashboardRef = useRef(dashboard);
  dashboardRef.current = dashboard;

  const save = (next) => {
    // Drop layout entries whose widget is gone, so a stale entry can never
    // resurrect a removed widget.
    const ids = new Set((next.widgets || []).map((w) => w.id));
    const layouts = {};
    for (const [bp, items] of Object.entries(next.layouts || {})) {
      layouts[bp] = (items || []).filter((it) => ids.has(it.i));
    }
    const clean = { ...next, layouts, version: 4 };
    dashboardRef.current = clean;
    updateData({ settings: { ...(data.settings || {}), dashboardLayout: clean } });
  };

  // Widgets that can actually render: a known variant whose feature is still
  // switched on in Settings. Disabling a feature hides its widgets without
  // deleting them, so re-enabling brings them back.
  const visibleWidgets = useMemo(
    () =>
      dashboard.widgets.filter(
        (w) => getWidgetBody(w.variant) && isWidgetVisible(w, data)
      ),
    [dashboard.widgets, data]
  );

  const onLayoutChange = (_current, allLayouts) => {
    if (!editMode) return;
    const base = dashboardRef.current;
    if (JSON.stringify(allLayouts) === JSON.stringify(base.layouts)) return;
    save({ ...base, layouts: allLayouts });
  };

  const addWidget = (variantId, scope, size) => {
    const variant = getVariant(variantId);
    if (!variant) return;
    const base = dashboardRef.current;
    const id = newWidgetId();
    const lg = [...(base.layouts?.lg || [])];
    const xs = [...(base.layouts?.xs || [])];
    lg.push({
      i: id,
      x: 0,
      y: bottomOf(lg),
      w: Math.min(size.w, COLS.lg),
      h: size.h,
      minW: variant.min?.w,
      minH: variant.min?.h
    });
    xs.push({ i: id, x: 0, y: bottomOf(xs), w: 1, h: mobileHeight(variant), minH: variant.min?.h });
    save({
      ...base,
      widgets: [...base.widgets, { id, variant: variantId, scope: scope || null }],
      layouts: { ...base.layouts, lg, xs }
    });
    // Jump into edit mode so the freshly added widget can be placed right away.
    setEditMode(true);
  };

  const removeWidget = (id) => {
    const base = dashboardRef.current;
    const layouts = {};
    for (const [bp, items] of Object.entries(base.layouts || {})) {
      layouts[bp] = (items || []).filter((it) => it.i !== id);
    }
    save({ ...base, widgets: base.widgets.filter((w) => w.id !== id), layouts });
  };

  const resetLayout = async () => {
    if (!(await confirm({ message: t('dashw.resetConfirm', 'Dashboard auf die Standard-Widgets zurücksetzen?'), danger: true }))) return;
    updateData({ settings: { ...(data.settings || {}), dashboardLayout: null } });
    setEditMode(false);
  };

  const sizeOf = (widgetId) => {
    const items = dashboard.layouts?.[breakpoint] || dashboard.layouts?.lg || [];
    const found = items.find((it) => it.i === widgetId);
    return found ? { w: found.w, h: found.h } : { w: 6, h: 8 };
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('nav.dashboard')}</h2>
          <p className="text-gray-500 capitalize">
            {formatDate(today, 'full', lang)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {editMode && (
            <button
              onClick={resetLayout}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <RotateCcw size={14} />
              {t('dashboard.reset', 'Layout zurücksetzen')}
            </button>
          )}
          <button
            onClick={() => setShowGallery(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:bg-gray-50"
          >
            <Plus size={14} />
            {t('dashw.widget', 'Widget')}
          </button>
          <button
            onClick={() => setEditMode((e) => !e)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              editMode
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:bg-gray-50'
            }`}
          >
            <SlidersHorizontal size={14} />
            {editMode ? t('dashboard.doneEditing', 'Fertig') : t('dashboard.customize', 'Anpassen')}
          </button>
        </div>
      </div>

      {editMode && (
        <p className="text-xs text-gray-500 bg-primary-50 dark:bg-gray-800 border border-primary-100 dark:border-gray-700 rounded-lg px-3 py-2">
          {t('dashboard.editHint', 'Widgets am Kopf ziehen, unten rechts in der Größe ändern, mit × entfernen.')}
        </p>
      )}

      {visibleWidgets.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
          <LayoutDashboard size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-500 mb-4">
            {t('dashw.emptyDashboard', 'Noch keine Widgets auf dem Dashboard.')}
          </p>
          <button
            onClick={() => setShowGallery(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={16} />
            {t('dashw.addWidget', 'Widget hinzufügen')}
          </button>
        </div>
      ) : (
        <ResponsiveGrid
          className="dashboard-grid -mx-2"
          layouts={dashboard.layouts}
          breakpoints={BREAKPOINTS}
          cols={COLS}
          rowHeight={ROW_HEIGHT}
          margin={[12, 12]}
          isDraggable={editMode}
          isResizable={editMode}
          draggableHandle=".widget-drag-handle"
          onLayoutChange={onLayoutChange}
          onBreakpointChange={setBreakpoint}
        >
          {visibleWidgets.map((w) => {
            const variant = getVariant(w.variant);
            const Body = getWidgetBody(w.variant);
            const subtitle = instanceLabel(w.variant, w.scope, data, t);
            const size = sizeOf(w.id);
            return (
              <div
                key={w.id}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden flex flex-col"
              >
                <div
                  className={`widget-drag-handle flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 select-none ${
                    editMode ? 'cursor-grab active:cursor-grabbing' : ''
                  }`}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    {editMode && <GripVertical size={14} className="text-gray-400 flex-shrink-0" />}
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">
                      {t(variant.labelKey, variant.fallback)}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 min-w-0">
                    {subtitle && (
                      <span className="text-xs text-gray-400 truncate max-w-[9rem]">{subtitle}</span>
                    )}
                    {editMode && (
                      <button
                        onClick={() => removeWidget(w.id)}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-700"
                        title={t('dashw.removeWidget', 'Widget entfernen')}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <Body
                    data={data}
                    updateData={updateData}
                    scope={w.scope}
                    size={size}
                    today={today}
                    t={t}
                    lang={lang}
                  />
                </div>
              </div>
            );
          })}
        </ResponsiveGrid>
      )}

      {showGallery && (
        <WidgetGallery
          data={data}
          t={t}
          onAdd={addWidget}
          onClose={() => setShowGallery(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
